import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { Plus, Upload, ClipboardList } from "lucide-react";

export function AddLeadsDialog({ client, open, onOpenChange, onDone, category = "umrah" }) {
  const [one, setOne] = useState({ name: "", phone: "", city: "" });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const report = (d) => { toast.success(`${d.added} added${d.skipped ? ` · ${d.skipped} skipped (duplicate/invalid)` : ""}`); onDone(); };
  const run = async (fn) => {
    setBusy(true);
    try { report((await fn()).data); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const addOne = () => run(() => client.post("/telecaller/leads", { ...one, category })).then(() => setOne({ name: "", phone: "", city: "" }));
  const addBulk = () => run(() => client.post("/telecaller/leads/bulk", { text, category })).then(() => setText(""));
  const upload = (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    run(() => client.post(`/telecaller/import?category=${category}`, fd, { headers: { "Content-Type": "multipart/form-data" } }));
    fileRef.current.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#05261D] border-gold/20 max-w-lg" data-testid="add-leads-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-gold">Add numbers — {category === "job" ? "Job customers" : category === "agent" ? "Agent leads" : "Umrah customers"}</DialogTitle>
          <DialogDescription>Duplicates are skipped automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <p className="eyebrow text-gold flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Single number</p>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <Input placeholder="Name" value={one.name} onChange={(e) => setOne({ ...one, name: e.target.value })} className="bg-card/60" data-testid="lead-add-name" />
              <Input placeholder="Mobile" value={one.phone} onChange={(e) => setOne({ ...one, phone: e.target.value })} className="bg-card/60 font-mono" data-testid="lead-add-phone" />
              <Input placeholder="City" value={one.city} onChange={(e) => setOne({ ...one, city: e.target.value })} className="bg-card/60" data-testid="lead-add-city" />
            </div>
            <Button onClick={addOne} disabled={busy || !one.phone.trim()} className="mt-2 bg-gold text-[#0a2e22] hover:bg-gold/90 h-8 text-xs" data-testid="lead-add-btn">Add</Button>
          </div>
          <div>
            <p className="eyebrow text-gold flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> Bulk paste</p>
            <Label className="text-[11px] text-muted-foreground">One per line — "Name, 98XXXXXXXX" or just numbers</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} className="mt-1 bg-card/60 min-h-[100px] font-mono text-xs" placeholder={"Abdul Rahim, 9830012345\n9876543210\nFatima 9123456789"} data-testid="lead-bulk-text" />
            <Button onClick={addBulk} disabled={busy || !text.trim()} className="mt-2 bg-gold text-[#0a2e22] hover:bg-gold/90 h-8 text-xs" data-testid="lead-bulk-btn">Add all</Button>
          </div>
          <div>
            <p className="eyebrow text-gold flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Import file</p>
            <Label className="text-[11px] text-muted-foreground">Excel (.xlsx), CSV or PDF — phone numbers (and names next to them) are picked up automatically</Label>
            <input ref={fileRef} type="file" accept=".xlsx,.csv,.pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="lead-import-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="mt-2 border-gold/40 text-gold h-8 text-xs gap-1.5" data-testid="lead-import-btn"><Upload className="w-3.5 h-3.5" /> Choose Excel / CSV / PDF</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
